'use client';

import { useLocale } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useTransition } from 'react';
import { FrenchFlag, BritishFlag } from '@/components/ui/FlagIcon';
import { areCookiesAllowed } from '@/lib/cookieConsent';

export function LanguageSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();

  const switchLocale = (newLocale: string) => {
    if (newLocale === locale) return;
    
    startTransition(() => {
      // Only set cookie if cookies are allowed (RGPD compliance)
      const cookiesAllowed = areCookiesAllowed();
      if (cookiesAllowed) {
        document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
      }
      
      // Replace locale in current pathname (works with or without cookies)
      const segments = pathname.split('/').filter(Boolean);
      if (segments[0] === locale) {
        segments[0] = newLocale;
      } else {
        segments.unshift(newLocale);
      }
      
      // Navigate to new path
      const newPath = `/${segments.join('/')}`;
      router.push(newPath);
    });
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => switchLocale('fr')}
        disabled={isPending || locale === 'fr'}
        className={`
          flex items-center gap-1.5 px-2 py-1 rounded-md transition-all
          ${locale === 'fr' 
            ? 'bg-accent/20 border border-accent/40' 
            : 'hover:bg-white/20 border border-transparent'
          }
          ${isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        aria-label="Switch to French"
        title="Français"
      >
        <FrenchFlag className="w-5 h-4" />
        <span className="text-sm font-medium text-foreground hidden sm:inline">FR</span>
      </button>
      <button
        onClick={() => switchLocale('en')}
        disabled={isPending || locale === 'en'}
        className={`
          flex items-center gap-1.5 px-2 py-1 rounded-md transition-all
          ${locale === 'en' 
            ? 'bg-accent/20 border border-accent/40' 
            : 'hover:bg-white/20 border border-transparent'
          }
          ${isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        aria-label="Switch to English"
        title="English"
      >
        <BritishFlag className="w-5 h-4" />
        <span className="text-sm font-medium text-foreground hidden sm:inline">EN</span>
      </button>
    </div>
  );
}

